import React, { useEffect, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { ParkedDomainType, useAddDomainForm } from 'cityads-v3/hooks/domainParking';
import { useDialogActions } from 'cityads-v3/hooks/dialog';
import classes from './AddDomain.scss';

import AddDomainSuccess from './AddDomainSuccess';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CopyText from 'cityads-v3/components/CopyText/CopyText';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import SelectField from 'cityads-v3/components/TextField/SelectField';
import TextField from 'cityads-v3/components/TextField/TextField';
import Typography from '@mui/material/Typography';
import WaitingButton from 'cityads-v3/components/Buttons/WaitingButton'

export type AddDomainProps = {
  Component?: React.FC<{ onClick: () => void; }>,
  domainType?: ParkedDomainType,
  onSuccess: () => void
}

const AddDomain: React.FC<AddDomainProps> = function AddDomain({
  onSuccess,
  Component,
  domainType = ParkedDomainType.links,
  ...rest
}) {
  const { t } = useTranslation(['DomainParking', 'main']);
  const [open, handleOpen, handleClose] = useDialogActions();
  const {
    domainTypes,
    form,
    formError,
    formEntityErrors,
    resetForm,
  } = useAddDomainForm({ domainType });

  const handleCloseSuccessDialog = useCallback(() => {
    handleClose();
    onSuccess?.();
  }, [handleClose, onSuccess])

  useEffect(() => {
    if (!open) {
      resetForm();
    }

    return resetForm
  }, [open, resetForm]);

  return (
    <>
      {Component ? <Component onClick={handleOpen} /> : (
        <Button
          color="primary"
          variant="contained"
          onClick={handleOpen}
          {...rest}
        >
          {t('addNewDomain')}
        </Button>
      )}
      <Dialog
        open={open}
        onClose={form.isSuccess ? handleCloseSuccessDialog : handleClose}
      >
        {form.isSuccess && <AddDomainSuccess onClose={handleCloseSuccessDialog} />}

        {!form.isSuccess && (
          <>
            <DialogTitle>{t('addNewDomainTitle')}</DialogTitle>
            <DialogContent>
              <DialogContentText component="div">
                <SelectField<number, number>
                  fullWidth
                  label={t('fields.domainType')}
                  items={domainTypes}
                  handleChange={form.fields.domainType.setValue}
                  value={form.fields.domainType.value}
                />

                <Box mt={2} />

                {form.fields.domainType.value === ParkedDomainType.links && (
                  <>
                    <Typography gutterBottom>{t('description')}</Typography>
                    <ol className={classes.instruction}>
                      <Typography component="li">
                        <Trans t={t} i18nKey={'variant1'}>
                          Add the CNAME entry with the address:
                          <CopyText component="span" color="primary" copyText="park.cityads.com">park.cityads.com</CopyText>
                          to your domain DNS editor, and add the domain to us.
                        </Trans>
                      </Typography>
                      <Typography component="li">
                        <Trans t={t} i18nKey={'variant2'}>
                          Add the A entry
                          <CopyText component="span" color="primary" copyText="195.161.21.222">195.161.21.222</CopyText>
                          to your domain DNS editor, and add the domain to us.
                        </Trans>
                      </Typography>
                    </ol>
                  </>
                )}

                {form.fields.domainType.value === ParkedDomainType.showcases && (
                  <>
                    <Typography gutterBottom>{t('descriptionShowcases')}</Typography>
                    <ol className={classes.instruction}>
                      <Typography component="li">
                        <Trans t={t} i18nKey={'variantShowcase1'}>
                          Add the CNAME entry with the address:
                          <CopyText component="span" color="primary" copyText="showcase.cityads.com">showcase.cityads.com</CopyText>
                          to your domain DNS editor, and add the domain to us.
                        </Trans>
                      </Typography>
                      <Typography component="li">
                        <Trans t={t} i18nKey={'variantShowcase2'}>
                          Add the A entry
                          <CopyText component="span" color="primary" copyText="195.161.21.223">195.161.21.223</CopyText>
                          to your domain DNS editor, and add the domain to us.
                        </Trans>
                      </Typography>
                    </ol>
                  </>
                )}
              </DialogContentText>

              <TextField
                fullWidth
                autoFocus
                error={form.fields.domain.error}
                helperText={form.fields.domain.errorText}
                label={t('fields.domain')}
                onChange={form.fields.domain.handleChange}
                value={form.fields.domain.value}
              />

              <Box mt={2} />

              <TextField
                fullWidth
                multiline
                label={t('fields.comment')}
                maxLength={300}
                maxRows={3}
                onChange={form.fields.comment.handleChange}
                value={form.fields.comment.value}
              />

              {formError ? (
                <Box mt={2}>
                  {formEntityErrors?.length ?
                    formEntityErrors.map(errorText => (
                      <Typography color="error" key={errorText}>{errorText}</Typography>
                    ))
                    : <Typography color="error">{t('main:forms.undefinedError')}</Typography>}
                </Box>
              ) : null}
            </DialogContent>

            <DialogActions>
              <Button onClick={handleClose} color="primary">
                {t('main:buttons.cancel')}
              </Button>
              <WaitingButton
                color="primary"
                delay={0}
                onClick={form.handleSubmit}
                variant="contained"
                waiting={form.isSending}
              >
                {t('formSubmit')}
              </WaitingButton>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  )
}

export default AddDomain;
